/**
 * Modo SLIM (`?slim=1`): a tela abre sem o menu principal — só o conteúdo. Serve
 * para o advogado tocar vários processos ao mesmo tempo, cada um na sua aba, com
 * toda a largura disponível para os autos.
 *
 * A marca vive na URL (e não numa rota paralela): assim ela sobrevive a refresh,
 * favorito e navegação interna, e qualquer tela de `/app` pode abrir nesse modo
 * sem duplicar rota nem provider.
 */
import { getAtuacao } from './delegacao/atuacao';

export const PARAM_SLIM = 'slim';

/** Marca uma URL do app como slim, preservando a query já existente. */
export function comModoSlim(pathname: string, search = ''): string {
  const params = new URLSearchParams(search);
  params.set(PARAM_SLIM, '1');
  return `${pathname}?${params.toString()}`;
}

/** URL dos autos digitais em modo slim. */
export function urlAutosSlim(numero: string, search = ''): string {
  return comModoSlim(`/app/processos/${encodeURIComponent(numero)}/autos`, search);
}

/** Nome da aba DESTE processo — reabrir reaproveita a que já existe, não empilha. */
export function nomeAbaAutos(numero: string): string {
  // Com o contexto no nome: a janela do mesmo processo aberta em OUTRA conta (a
  // própria, ou a de outro titular) tem o próprio sessionStorage e não pode ser
  // reaproveitada — o trabalho cairia na conta errada.
  const conta = getAtuacao()?.titularId.replace(/\W/g, '');
  return `jurisapp_autos_${numero.replace(/\D/g, '')}${conta ? `_por_${conta}` : ''}`;
}

/**
 * Abre (ou reaproveita) a aba dos autos deste processo. O alvo nomeado é o que
 * evita a terceira aba do mesmo processo quando o advogado clica de novo.
 * Devolve `false` quando o navegador bloqueia a abertura, para o chamador poder
 * avisar em vez de deixar o clique sem resposta.
 */
export function abrirAutosEmNovaAba(numero: string, search = ''): boolean {
  // Primeiro SEM url: se a aba deste processo já está aberta nos autos dele, só
  // ganha o foco. Abrir com a url navegava a aba existente — ela recarregava e perdia
  // a peça aberta, a rolagem e o assistente. Sem aba (ou noutra tela), a vazia que o
  // navegador acabou de criar é levada aos autos.
  const aba = window.open('', nomeAbaAutos(numero));
  if (!aba) return false;
  let jaNosAutos = false;
  try {
    jaNosAutos = aba.location.pathname.startsWith(`/app/processos/${encodeURIComponent(numero)}/autos`);
  } catch {
    // Outra origem (não acontece com as nossas abas): trata como aba nova.
  }
  // Com uma entrada (peticionar com rascunho ou modelo), a aba vai a ela mesmo já
  // nos autos: só o foco perderia o pedido.
  if (!jaNosAutos || search) aba.location.href = urlAutosSlim(numero, search);
  aba.focus();
  return true;
}

/** URL da página de UM documento, em modo janela. */
export function urlDocumentoSlim(numero: string, uuid: string): string {
  return comModoSlim(
    `/app/processos/${encodeURIComponent(numero)}/documentos/${encodeURIComponent(uuid)}`,
  );
}

/**
 * Abre UM DOCUMENTO em janela própria — o caminho para ler duas peças lado a
 * lado.
 *
 * Vai para a página do DOCUMENTO, não para os autos com `?doc=`: quem pede para
 * abrir a peça quer a peça, e reabrir a tela inteira (lista, assistente,
 * cabeçalho) só para exibir um arquivo é caro e desorienta.
 *
 * O alvo nomeado inclui o documento de propósito: `nomeAbaAutos` é por processo,
 * então a segunda peça cairia na MESMA janela e substituiria a primeira — o
 * oposto do que a comparação pede. Reabrir o MESMO documento continua caindo na
 * janela dele, em vez de empilhar cópias.
 */
export function abrirDocumentoEmNovaAba(numero: string, uuid: string): boolean {
  const aba = window.open(
    urlDocumentoSlim(numero, uuid),
    `${nomeAbaAutos(numero)}_${uuid.replace(/\W/g, '')}`,
  );
  if (!aba) return false;
  aba.focus();
  return true;
}
